// Tableau de bord adapté selon le type d'utilisateur
// Fichier: /var/www/squarecom/frontend/src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Button,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Build,
  Assignment,
  CheckCircle,
  Schedule,
  Warning,
  TrendingUp,
  People,
  Home,
  Star,
  Refresh,
  ArrowForward,
  CalendarToday,
  LocationOn,
  Engineering,
  AssignmentTurnedIn,
  PendingActions,
  Speed,
  BugReport,
  Groups,
  Apartment,
  Timeline,
  AccountBalance
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [recentInterventions, setRecentInterventions] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const navigate = useNavigate();
  const theme = useTheme();

  // Récupérer les infos utilisateur
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role;
  const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Utilisateur';

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 60000); // Rafraîchir chaque minute
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Charger les interventions
      const intResponse = await fetch('/api/interventions/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const intData = await intResponse.json();
      const interventions = intData.interventions || [];

      // Calculer les statistiques selon le rôle
      const userStats = calculateStatsByRole(interventions, userRole, user.id);
      setStats(userStats);

      // Interventions récentes
      const recent = interventions
        .filter(i => filterByRole(i, userRole, user.id))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      setRecentInterventions(recent);

      // Tâches à venir pour techniciens
      if (userRole === 'technician' || userRole === 'contractor') {
        const upcoming = interventions
          .filter(i => i.assignedTo === user.id && i.status === 'assigned')
          .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
          .slice(0, 5);
        setUpcomingTasks(upcoming);
      }

    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterByRole = (intervention, role, userId) => {
    switch(role) {
      case 'superadmin':
      case 'admin':
      case 'manager':
        return true; // Voir toutes les interventions
      case 'technician':
      case 'contractor':
        return intervention.assignedTo === userId;
      case 'tenant':
        return intervention.reportedBy === userId;
      default:
        return false;
    }
  };

  const calculateStatsByRole = (interventions, role, userId) => {
    const filtered = interventions.filter(i => filterByRole(i, role, userId));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thisWeek = new Date();
    thisWeek.setDate(thisWeek.getDate() - 7);
    const thisMonth = new Date();
    thisMonth.setDate(1);

    switch(role) {
      case 'superadmin':
      case 'admin':
        return {
          total: interventions.length,
          urgent: interventions.filter(i => i.priority === 'urgent' || i.priority === 'high').length,
          pending: interventions.filter(i => i.status === 'pending').length,
          assigned: interventions.filter(i => i.status === 'assigned').length,
          inProgress: interventions.filter(i => i.status === 'in_progress').length,
          completed: interventions.filter(i => i.status === 'completed').length,
          thisWeek: interventions.filter(i => new Date(i.createdAt) >= thisWeek).length,
          resolutionRate: Math.round((interventions.filter(i => i.status === 'completed').length / interventions.length) * 100) || 0,
          avgResolutionTime: '2.5 jours',
          satisfaction: '4.5/5'
        };

      case 'manager':
        return {
          total: filtered.length,
          pending: filtered.filter(i => i.status === 'pending').length,
          assigned: filtered.filter(i => i.status === 'assigned').length,
          inProgress: filtered.filter(i => i.status === 'in_progress').length,
          completed: filtered.filter(i => i.status === 'completed').length,
          thisWeek: filtered.filter(i => new Date(i.createdAt) >= thisWeek).length,
          urgent: filtered.filter(i => i.priority === 'urgent' || i.priority === 'high').length,
          technicianCount: new Set(filtered.map(i => i.assignedTo).filter(Boolean)).size
        };

      case 'technician':
      case 'contractor':
        return {
          myTotal: filtered.length,
          myPending: filtered.filter(i => i.status === 'assigned').length,
          myInProgress: filtered.filter(i => i.status === 'in_progress').length,
          myCompleted: filtered.filter(i => i.status === 'completed').length,
          myUrgent: filtered.filter(i => i.priority === 'urgent' || i.priority === 'high').length,
          todayTasks: filtered.filter(i => {
            const schedDate = new Date(i.scheduledDate);
            return schedDate >= today && schedDate < new Date(today.getTime() + 24*60*60*1000);
          }).length,
          thisWeek: filtered.filter(i => {
            const schedDate = new Date(i.scheduledDate);
            return schedDate >= today && schedDate <= new Date(today.getTime() + 7*24*60*60*1000);
          }).length,
          completedThisMonth: filtered.filter(i =>
            i.status === 'completed' && new Date(i.completedDate) >= thisMonth
          ).length
        };

      case 'tenant':
        return {
          myRequests: filtered.length,
          pending: filtered.filter(i => i.status === 'pending' || i.status === 'assigned').length,
          inProgress: filtered.filter(i => i.status === 'in_progress').length,
          resolved: filtered.filter(i => i.status === 'completed').length,
          urgent: filtered.filter(i => i.priority === 'urgent' || i.priority === 'high').length,
          avgResponseTime: '4 heures',
          satisfaction: filtered.filter(i => i.rating >= 4).length
        };

      default:
        return {};
    }
  };

  const getDashboardConfig = () => {
    switch(userRole) {
      case 'superadmin':
      case 'admin':
        return {
          title: 'Tableau de bord Administrateur',
          subtitle: 'Vue d\'ensemble complète du système',
          cards: [
            {
              label: 'Total Interventions',
              value: stats.total || 0,
              icon: <Build />,
              color: 'primary',
              bgcolor: 'primary.light',
              trend: '+12%'
            },
            {
              label: 'Urgences',
              value: stats.urgent || 0,
              icon: <Warning />,
              color: 'error',
              bgcolor: 'error.light',
              subtitle: 'À traiter en priorité'
            },
            {
              label: 'En attente',
              value: stats.pending || 0,
              icon: <Schedule />,
              color: 'warning',
              bgcolor: 'warning.light'
            },
            {
              label: 'En cours',
              value: stats.inProgress || 0,
              icon: <Engineering />,
              color: 'info',
              bgcolor: 'info.light'
            },
            {
              label: 'Taux de résolution',
              value: `${stats.resolutionRate || 0}%`,
              icon: <TrendingUp />,
              color: 'success',
              bgcolor: 'success.light'
            },
            {
              label: 'Satisfaction',
              value: stats.satisfaction || 'N/A',
              icon: <Star />,
              color: 'secondary',
              bgcolor: 'secondary.light'
            }
          ],
          showAllStats: true
        };

      case 'manager':
        return {
          title: 'Tableau de bord Gestionnaire',
          subtitle: `Gestion des interventions et équipes`,
          cards: [
            {
              label: 'Total à gérer',
              value: stats.total || 0,
              icon: <Assignment />,
              color: 'primary',
              bgcolor: 'primary.light'
            },
            {
              label: 'En attente',
              value: stats.pending || 0,
              icon: <PendingActions />,
              color: 'warning',
              bgcolor: 'warning.light',
              action: () => navigate('/interventions')
            },
            {
              label: 'Assignées',
              value: stats.assigned || 0,
              icon: <AssignmentTurnedIn />,
              color: 'info',
              bgcolor: 'info.light'
            },
            {
              label: 'Cette semaine',
              value: stats.thisWeek || 0,
              icon: <CalendarToday />,
              color: 'secondary',
              bgcolor: 'secondary.light'
            },
            {
              label: 'Techniciens actifs',
              value: stats.technicianCount || 0,
              icon: <Groups />,
              color: 'success',
              bgcolor: 'success.light'
            },
            {
              label: 'Urgences',
              value: stats.urgent || 0,
              icon: <Warning />,
              color: 'error',
              bgcolor: 'error.light'
            }
          ]
        };

      case 'technician':
      case 'contractor':
        return {
          title: 'Mes Interventions',
          subtitle: `Bienvenue ${userName}`,
          cards: [
            {
              label: 'Mes interventions',
              value: stats.myTotal || 0,
              icon: <Build />,
              color: 'primary',
              bgcolor: 'primary.light'
            },
            {
              label: 'À faire aujourd\'hui',
              value: stats.todayTasks || 0,
              icon: <CalendarToday />,
              color: 'error',
              bgcolor: 'error.light',
              important: true
            },
            {
              label: 'En attente',
              value: stats.myPending || 0,
              icon: <Schedule />,
              color: 'warning',
              bgcolor: 'warning.light'
            },
            {
              label: 'En cours',
              value: stats.myInProgress || 0,
              icon: <Engineering />,
              color: 'info',
              bgcolor: 'info.light'
            },
            {
              label: 'Cette semaine',
              value: stats.thisWeek || 0,
              icon: <CalendarToday />,
              color: 'secondary',
              bgcolor: 'secondary.light'
            },
            {
              label: 'Terminées ce mois',
              value: stats.completedThisMonth || 0,
              icon: <CheckCircle />,
              color: 'success',
              bgcolor: 'success.light'
            }
          ],
          showUpcomingTasks: true
        };

      case 'tenant':
        return {
          title: 'Mon Espace Locataire',
          subtitle: `Bienvenue ${userName}`,
          cards: [
            {
              label: 'Mes demandes',
              value: stats.myRequests || 0,
              icon: <BugReport />,
              color: 'primary',
              bgcolor: 'primary.light'
            },
            {
              label: 'En attente',
              value: stats.pending || 0,
              icon: <Schedule />,
              color: 'warning',
              bgcolor: 'warning.light'
            },
            {
              label: 'En cours',
              value: stats.inProgress || 0,
              icon: <Build />,
              color: 'info',
              bgcolor: 'info.light'
            },
            {
              label: 'Résolues',
              value: stats.resolved || 0,
              icon: <CheckCircle />,
              color: 'success',
              bgcolor: 'success.light'
            }
          ],
          showQuickActions: true
        };

      default:
        return {
          title: 'Tableau de bord',
          cards: []
        };
    }
  };

  const config = getDashboardConfig();

  const getPriorityColor = (priority) => ({
    urgent: 'error',
    high: 'warning',
    medium: 'info',
    low: 'default'
  }[priority] || 'default');

  const getPriorityLabel = (priority) => ({
    urgent: 'Urgent',
    high: 'Haute',
    medium: 'Moyenne',
    low: 'Faible'
  }[priority] || priority);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4 }}>
          <LinearProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {config.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {config.subtitle}
        </Typography>
      </Box>

      {/* Cartes de statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {config.cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                cursor: card.action ? 'pointer' : 'default',
                transition: 'all 0.3s',
                '&:hover': card.action ? {
                  borderColor: `${card.color}.main`,
                  transform: 'translateY(-4px)',
                  boxShadow: 2
                } : {}
              }}
              onClick={card.action}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{
                    bgcolor: (theme) => theme.palette[card.color].main + '20',  // Transparence de 8% pour un fond très clair
                    color: `${card.color}.main`,
                    width: 40,
                    height: 40
                  }}>
                    {card.icon}
                  </Avatar>
                  {card.trend && (
                    <Chip
                      label={card.trend}
                      size="small"
                      color="success"
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Contenu principal */}
      <Grid container spacing={3}>
        {/* Interventions récentes */}
        <Grid item xs={12} md={config.showUpcomingTasks ? 6 : 8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Interventions récentes
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/interventions')}
              >
                Voir tout
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {recentInterventions.length === 0 ? (
              <Alert severity="info">Aucune intervention récente</Alert>
            ) : (
              <List>
                {recentInterventions.map((intervention, index) => (
                  <ListItem
                    key={intervention.id}
                    sx={{
                      px: 0,
                      borderBottom: index < recentInterventions.length - 1 ? '1px solid' : 'none',
                      borderColor: 'grey.100'
                    }}
                  >
                    <ListItemIcon>
                      {(intervention.priority === 'urgent' || intervention.priority === 'high') ?
                        <Warning color="error" /> :
                        <Build color="action" />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {intervention.title}
                          </Typography>
                          <Chip
                            label={getPriorityLabel(intervention.priority)}
                            size="small"
                            color={getPriorityColor(intervention.priority)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <LocationOn fontSize="small" />
                          <Typography variant="caption">
                            {intervention.location || intervention.residenceName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • {new Date(intervention.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Tâches à venir (pour techniciens) */}
        {config.showUpcomingTasks && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Mes prochaines tâches
                </Typography>
                <IconButton size="small" onClick={loadDashboardData}>
                  <Refresh />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {upcomingTasks.length === 0 ? (
                <Alert severity="info">Aucune tâche programmée</Alert>
              ) : (
                <List>
                  {upcomingTasks.map((task) => (
                    <ListItem key={task.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Schedule color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Box>
                            <Typography variant="caption">
                              {new Date(task.scheduledDate).toLocaleString('fr-FR')}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {task.location}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        )}

        {/* Actions rapides */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Actions rapides
            </Typography>
            <Stack spacing={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Build />}
                onClick={() => navigate('/scan/new')}
              >
                Nouvelle intervention
              </Button>
              {(userRole === 'admin' || userRole === 'superadmin' || userRole === 'manager') && (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<People />}
                    onClick={() => navigate('/users')}
                  >
                    Gérer les utilisateurs
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Home />}
                    onClick={() => navigate('/residences')}
                  >
                    Gérer les résidences
                  </Button>
                </>
              )}
              {userRole === 'tenant' && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BugReport />}
                  onClick={() => window.open('/scan', '_blank')}
                >
                  Scanner un QR Code
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;
